import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { mutateCreateMessageReport } from "@services/ConversationsService";

import { useToast } from "@hooks/useToast";

import Button from "@ui-components/input/Button";
import { Modal } from "@ui-components/layout/Modal";

import styles from './ReportMessageModal.module.css';
import TextArea from "./TextArea";

const messages = defineMessages({
  title: {
    id: "reportMessage.title",
    defaultMessage: "Report a problem with a bot response?",
    description: "Title of the modal for reporting an issue with a bot-generated message",
  },
  fieldLabel: {
    id: "reportMessage.fieldLabel",
    defaultMessage: "Additional context",
    description: "Label for the textarea where the user can write optional context for the report",
  },
  placeholder: {
    id: "reportMessage.placeholder",
    defaultMessage: "Additional information about the issue",
    description: "Placeholder text inside the textarea explaining what type of context the user can add",
  },
  cancel: {
    id: "global.cancel",
    defaultMessage: "Cancel",
    description: "Reusable label for cancel buttons",
  },
  submit: {
    id: "reportMessage.submit",
    defaultMessage: "Submit report",
    description: "Label for the button that submits the bot message report",
  },
  success: {
    id: "reportMessage.success",
    defaultMessage: "Problem report submitted.",
    description: "Toast notification shown when a bot message report is successfully submitted",
  },
  error: {
    id: 'reportMessage.error',
    defaultMessage: 'Failed to submit problem report. Please try again.',
    description: 'Toast shown when submitting report fails',
  },
});

type Props = {
  messageId: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export default function ReportMessageModal({ messageId, open, onOpenChange }: Props) {
  const intl = useIntl();
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await mutateCreateMessageReport(messageId, comment);
      toast('success', intl.formatMessage(messages.success));
      setComment("");
      onOpenChange?.(false);
    } catch {
      toast('error', intl.formatMessage(messages.error));
    } finally {
      setSubmitting(false);
    }
  };

  const Body = (
    <div className={styles.body}>
      <FormattedMessage
        id="reportMessage.blurb"
        defaultMessage="This message will be flagged for review. Please provide additional context that can help, for example: the answer is in the wrong language, the information is not in the sources (or is there but misinterpreted), the wrong section or timespan of the sources was used (tell us which one you expected), links or resources do not match your personalized settings, or the answer did not address your question."
        description="Instructional text shown in the modal describing what happens when reporting a message"
        tagName="p"
      />

      <TextArea
        id="report-textarea"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        label={intl.formatMessage(messages.fieldLabel)}
        placeholder={intl.formatMessage(messages.placeholder)}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        size="small"
        rows={2}
      />
    </div>
  );

  const Actions = (
    <>
      <Button
        variant="text"
        label={intl.formatMessage(messages.cancel)}
        onClick={() => {
          setComment("");
          onOpenChange?.(false);
        }}
        disabled={submitting}
        size="small"
      />
      <Button
        variant="filled"
        label={intl.formatMessage(messages.submit)}
        onClick={handleSubmit}
        disabled={submitting}
        size="small"
      />
    </>
  );

  return (
    <>
      <Modal
        size="small"
        title={intl.formatMessage(messages.title)}
        body={Body}
        open={open}
        onOpenChange={onOpenChange}
        actions={Actions}
      />
    </>
  );
}
